import React, { useEffect, useState } from "react";
import { Badge, Tabs } from "flowbite-react";
import SampleTestCaseTable from "./SampleTestCaseTable";
import { HiClipboardList, HiUserCircle } from "react-icons/hi";
import { useGetLastFiveSubmissionsQuery } from "../../../graphql/generated.tsx";
import Loader from "../../../components/Loader.jsx";
import SubmissionList from "./SubmissionList.jsx";

const CompilerQuestionCard = ({
  question,
  loading,
  onApply,
  hideSubmission,
  extractFunctionCode,
  showFunctionOnly,
  targetFunctionName,
  submissionsProps,
  submitInput,
  isFullScreen,
  isCompleted: isCompletedProp,
}) => {
  const [submissions, setSubmissions] = useState([]);

  const { data } = useGetLastFiveSubmissionsQuery({
    variables: { submitInput },
    skip: !submitInput?.campus_name ||
      !submitInput?.cluster_id ||
      !submitInput?.student_id ||
      !submitInput?.course_id ||
      !submitInput?.question_id,
     errorPolicy: "ignore", 
  });

  useEffect(() => {
    if (submissionsProps) {
      const sortedSubmissions = [...submissionsProps].sort(
        (a, b) => new Date(b.submittedAt) - new Date(a.submittedAt)
      );
      setSubmissions(sortedSubmissions);
    } else if (data?.getLastFiveSubmissions) {
      const sortedSubmissions = [
        ...data.getLastFiveSubmissions.submissions,
      ].sort((a, b) => new Date(b.submittedAt) - new Date(a.submittedAt));
      setSubmissions(sortedSubmissions);
    }
  }, [data, question]);

  const onCopyHandler = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  if (loading) {
    return (
      <div className="items-center p-8 bg-white rounded-md shadow-md"></div>
    );
  }

  let isCompleted = data?.getLastFiveSubmissions?.is_completed;
  if (typeof isCompletedProp === "boolean") {
    isCompleted = isCompletedProp;
  }

  const highlightCodeTags = (html) => {
    return (
      html &&
      html.replace(/<code[^>]*>(.*?)<\/code>/gs, (match, content) => {
        return `<code class="bg-gray-200 text-sm rounded font-mono p-1 not-prose">${content}</code>`;
      })
    );
  };

  return (
    <Tabs aria-label="Tabs with underline" variant="fullWidth">
      <Tabs.Item active title="Question" icon={HiUserCircle}>
        <div
          className="relative p-8 bg-white rounded-md shadow-md"
          onCopy={onCopyHandler}
        >
          {(() => {
            const reserve = isFullScreen && isCompleted;
            return (
              <h3
                className={`text-2xl font-semibold ${reserve ? 'mr-40 max-md:mr-0' : ''}`}
              >
                {question?.title}
              </h3>
            );
          })()}
          <div className="w-full py-2 prose max-w-none custom-bullet-list">
            <div
              className="box-container"
              dangerouslySetInnerHTML={{
                __html: highlightCodeTags(question?.text),
              }}
            />
          </div>

          {isCompleted && (
            <Badge
              color="green"
              className="absolute top-8 right-8 max-md:top-0"
            >
              Completed
            </Badge>
          )}

          <h4 className="mt-4 text-xl font-semibold"></h4>
          <div className="w-1/2 max-md:w-full">
            <SampleTestCaseTable question={question} />
          </div>

          {question?.hints?.length > 0 && (
            <div className="mt-4">
              <span className="font-semibold text-gray-600 dark:text-gray-400">
                Hints:
              </span>
              <ul>
                {question.hints.map(
                  (hint, index) =>
                    hint && (
                      <li key={index}>
                        {index + 1}. {hint}
                      </li>
                    )
                )}
              </ul>
            </div>
          )}
        </div>
      </Tabs.Item>

      {!hideSubmission && (
        <Tabs.Item title="Submissions" icon={HiClipboardList}>
          <SubmissionList
            submissions={submissions}
            onApply={(submissionCode, submissionLanguage) => {
              onApply(submissionCode, submissionLanguage);

              setTimeout(() => {
                const targetElement =
                  document.getElementById("compiler-section") ||
                  document.querySelector('[class*="compiler"]') ||
                  document.querySelector('[class*="Compiler"]') ||
                  document.querySelector(".CodeMirror") ||
                  document.querySelector("textarea");

                if (targetElement) {
                  targetElement.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                  });
                } else {
                  window.scrollTo({
                    top: window.innerHeight / 2,
                    behavior: "smooth",
                  });
                }
              }, 200);
            }}
            showFunctionOnly={showFunctionOnly}
            extractFunctionCode={extractFunctionCode}
            targetFunctionName={targetFunctionName}
          />
        </Tabs.Item>
      )}
    </Tabs>
  );
};

export default CompilerQuestionCard;
